import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { useLang } from '../LanguageContext';
import { t } from '../i18n';

const COULEUR = '#C0392B';

export default function ProfilCommercial({ user, onLogout, onClose }) {
  const { lang, isRTL } = useLang();
  const [visites, setVisites] = useState([]);
  const [actions, setActions] = useState([]);
  const [assignation, setAssignation] = useState(null);
  const [objectifs, setObjectifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const vSnap = await getDocs(query(collection(db, 'visites'), where('commercialId', '==', user.uid)));
      setVisites(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const aSnap = await getDocs(query(collection(db, 'actions'), where('commercialId', '==', user.uid)));
      setActions(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const asSnap = await getDocs(query(collection(db, 'assignations'), where('commercialId', '==', user.uid)));
      if (asSnap.docs.length > 0) setAssignation({ id: asSnap.docs[0].id, ...asSnap.docs[0].data() });
      const oSnap = await getDocs(collection(db, 'objectifs'));
      setObjectifs(oSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const maintenant = new Date();
  const debutMois = new Date(maintenant.getFullYear(), maintenant.getMonth(), 1);

  const visitesThisMois = visites.filter(v => v.heureDebut && new Date(v.heureDebut) >= debutMois);
  const actionsEnRetard = actions.filter(a => a.statut === 'A FAIRE' && a.dateAction && new Date(a.dateAction) < maintenant);
  const actionsFaites = actions.filter(a => a.statut === 'FAIT');

  const objectifVisites = user.objectifVisites || 60;
  const scoreVisites = Math.round((visitesThisMois.length / objectifVisites) * 100);
  const varTotal = assignation?.variableTotal || user.variableTotal || 0;
  const primeEstimee = Math.round(varTotal * Math.min(scoreVisites, 100) / 100);

  // Résultats visites ce mois
  const resultatsMap = {};
  visitesThisMois.forEach(v => {
    const r = v.resultat || 'Non renseigné';
    resultatsMap[r] = (resultatsMap[r] || 0) + 1;
  });

  // Visites par jour cette semaine
  const visitesParJour = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date();
    const jour = d.getDay();
    const samedi = new Date(d);
    samedi.setDate(d.getDate() - ((jour + 1) % 7) + i);
    const count = visites.filter(v => v.heureDebut && new Date(v.heureDebut).toDateString() === samedi.toDateString()).length;
    visitesParJour.push({ label: ['Sam', 'Dim', 'Lun', 'Mar', 'Mer', 'Jeu'][i], count });
  }
  const maxV = Math.max(...visitesParJour.map(v => v.count), 1);

  const handleLogout = async () => { await signOut(auth); onLogout(); };

  const moisNom = maintenant.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>{t(lang, 'mon_profil')}</div>
          <div style={styles.topbarSub}>{moisNom}</div>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>{t(lang, 'deconnexion')}</button>
      </div>

      <div style={styles.content}>

        {/* AVATAR & INFO */}
        <div style={styles.profileCard}>
          <div style={styles.avatar}>{(user.prenom || user.nom || 'C')[0].toUpperCase()}</div>
          <div style={styles.profileName}>{user.prenom} {user.nom}</div>
          <div style={styles.profileRole}>Commercial · KDPeinture</div>
          <div style={styles.profileWilaya}>📍 {(user.wilayas || ['Constantine']).join(' · ')}</div>
        </div>

        {/* SCORE DU MOIS */}
        <div style={styles.scoreCard}>
          <div style={styles.scoreHeader}>
            <div>
              <div style={styles.scoreTitle}>t(lang, 'score_month')</div>
              <div style={styles.scoreSub}>{visitesThisMois.length} / {objectifVisites} visites</div>
            </div>
            <div style={{ ...styles.scorePct, color: scoreVisites >= 80 ? '#3B6D11' : scoreVisites >= 50 ? '#854F0B' : COULEUR }}>
              {scoreVisites}%
            </div>
          </div>
          <div style={styles.scoreBar}>
            <div style={{ ...styles.scoreBarFill, width: `${Math.min(scoreVisites, 100)}%`, background: scoreVisites >= 80 ? '#3B6D11' : scoreVisites >= 50 ? '#E67E22' : COULEUR }}></div>
          </div>
          <div style={styles.primeEstimee}>
            <span style={styles.primeLabel}>t(lang, 'prime_estimee')</span>
            <span style={styles.primeVal}>{primeEstimee.toLocaleString()} DA</span>
          </div>
        </div>

        {/* STATS RAPIDES */}
        <div style={styles.statsGrid}>
          {[
            { val: visitesThisMois.length, label: 'Visites ce mois', color: '#1C2833', icon: '🚶' },
            { val: visites.length, label: 'Total visites', color: '#185FA5', icon: '📋' },
            { val: actionsEnRetard.length, label: 'Actions en retard', color: '#C0392B', icon: '⚠️' },
            { val: actionsFaites.length, label: 'Actions faites', color: '#3B6D11', icon: '✅' },
          ].map((s, i) => (
            <div key={i} style={styles.statCard}>
              <div style={styles.statIcon}>{s.icon}</div>
              <div style={{ ...styles.statVal, color: s.color }}>{s.val}</div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* GRAPHIQUE SEMAINE */}
        <div style={styles.sectionLabel}>Visites cette semaine</div>
        <div style={styles.graphCard}>
          <div style={styles.barChart}>
            {visitesParJour.map((v, i) => (
              <div key={i} style={styles.barItem}>
                <div style={styles.barWrap}>
                  <div style={{ ...styles.bar, height: `${Math.round((v.count / maxV) * 100)}%`, background: v.count > 0 ? COULEUR : '#e0e0e0' }}>
                    {v.count > 0 && <div style={styles.barVal}>{v.count}</div>}
                  </div>
                </div>
                <div style={styles.barLabel}>{v.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* OBJECTIFS */}
        {assignation && objectifs.length > 0 && (
          <>
            <div style={styles.sectionLabel}>Mes objectifs du mois</div>
            <div style={styles.objCard}>
              {objectifs.map((o, i) => {
                const ratio = assignation.objectifsRatios?.[o.id] || 0;
                const montant = Math.round(varTotal * ratio / 100);
                const COLORS = ['#C0392B', '#185FA5', '#3B6D11', '#854F0B', '#1D9E75'];
                return ratio > 0 ? (
                  <div key={o.id} style={styles.objRow}>
                    <div style={{ ...styles.objDot, background: COLORS[i % COLORS.length] }}></div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.objNom}>{o.designation}</div>
                      <div style={styles.objSub}>{o.objectif} {o.unite} {o.periode} · Min. {o.tauxMin}%</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS[i % COLORS.length] }}>{ratio}%</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{montant.toLocaleString()} DA</div>
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          </>
        )}

        {/* RÉSULTATS VISITES */}
        {Object.keys(resultatsMap).length > 0 && (
          <>
            <div style={styles.sectionLabel}>Résultats de mes visites</div>
            <div style={styles.resultatCard}>
              {Object.entries(resultatsMap).sort((a, b) => b[1] - a[1]).map(([r, count], i) => (
                <div key={i} style={styles.resultatRow}>
                  <div style={{ fontSize: 13, flex: 1, color: '#1C2833' }}>{r}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2833', marginRight: 8 }}>{count}</div>
                  <div style={styles.resultatBarWrap}>
                    <div style={{ ...styles.resultatBar, width: `${Math.round((count / visitesThisMois.length) * 100)}%` }}></div>
                  </div>
                  <div style={{ fontSize: 11, color: '#888', minWidth: 30, textAlign: 'right' }}>
                    {Math.round((count / visitesThisMois.length) * 100)}%
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* DÉCONNEXION */}
        <button style={styles.btnLogout} onClick={handleLogout}>
          🚪 Se déconnecter
        </button>

      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: COULEUR, padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.8, marginTop: 2 },
  logoutBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 11, padding: '5px 10px', cursor: 'pointer', flexShrink: 0 },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 },
  profileCard: { background: '#fff', borderRadius: 12, padding: '20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  avatar: { width: 60, height: 60, borderRadius: '50%', background: COULEUR, color: '#fff', fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' },
  profileName: { fontSize: 17, fontWeight: 700, color: '#1C2833' },
  profileRole: { fontSize: 13, color: '#888', marginTop: 4 },
  profileWilaya: { fontSize: 12, color: '#666', marginTop: 6 },
  scoreCard: { background: '#fff', borderRadius: 12, padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  scoreHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  scoreTitle: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  scoreSub: { fontSize: 12, color: '#888', marginTop: 2 },
  scorePct: { fontSize: 28, fontWeight: 700 },
  scoreBar: { height: 8, background: '#f0f0f0', borderRadius: 99, marginBottom: 12 },
  scoreBarFill: { height: 8, borderRadius: 99, transition: 'width 0.5s' },
  primeEstimee: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#EAF3DE', borderRadius: 8, padding: '8px 12px' },
  primeLabel: { fontSize: 12, color: '#3B6D11' },
  primeVal: { fontSize: 16, fontWeight: 700, color: '#3B6D11' },
  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 },
  statCard: { background: '#fff', borderRadius: 10, padding: '10px 6px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  statIcon: { fontSize: 16, marginBottom: 4 },
  statVal: { fontSize: 18, fontWeight: 700 },
  statLabel: { fontSize: 9, color: '#888', marginTop: 2, lineHeight: 1.3 },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  graphCard: { background: '#fff', borderRadius: 10, padding: '14px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  barChart: { display: 'flex', gap: 6, alignItems: 'flex-end', height: 70 },
  barItem: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  barWrap: { width: '100%', height: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  bar: { width: '100%', borderRadius: '4px 4px 0 0', position: 'relative', minHeight: 4, transition: 'height 0.3s' },
  barVal: { position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)', fontSize: 10, fontWeight: 600, color: '#1C2833' },
  barLabel: { fontSize: 10, color: '#888' },
  objCard: { background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 10 },
  objRow: { display: 'flex', alignItems: 'center', gap: 10 },
  objDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  objNom: { fontSize: 13, fontWeight: 500, color: '#1C2833' },
  objSub: { fontSize: 11, color: '#888', marginTop: 2 },
  resultatCard: { background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 8 },
  resultatRow: { display: 'flex', alignItems: 'center', gap: 8 },
  resultatBarWrap: { flex: 1, height: 5, background: '#f0f0f0', borderRadius: 99 },
  resultatBar: { height: 5, background: COULEUR, borderRadius: 99 },
  btnLogout: { width: '100%', padding: 12, background: '#FCEBEB', border: 'none', borderRadius: 10, color: '#A32D2D', fontSize: 14, fontWeight: 500, cursor: 'pointer', marginTop: 8 },
};
